/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

/**
 * Migrated from: lego/page/etmf/list/apply-package/step1/package-contents/PackageContents.jsx
 *
 * Package contents management component for ETMF package configuration.
 * Handles entry selection, validation, duplication, and removal.
 */

import React, { useEffect, useState } from 'react'
import { Formik } from 'formik'
import { clone, isEqual, uniq, difference, each, chain, find, filter, findIndex, sortBy } from 'underscore'
import { Select } from '~/components/Select/Select'
import { useServiceContext } from '~/root'
import { useModal } from '~/contexts/ModalContext'
import Utils from '~/lib/Utils'
import PackageRemoveConfirmationModal from './PackageRemoveConfirmationModal'
import AddFileplanArtifactModal from './AddFileplanArtifactModal'
import TableMenu from './TableMenu'

interface PackageEntry {
  id: number
  zone: string
  section: string
  alias: string
  name?: string
  selected?: boolean
  docTypeId: number | string
  docTypeIdOrig?: number | string
  isCopy?: boolean
  isUnique?: boolean
  fileName?: string
}

interface PackageData {
  id?: number
  level?: string
  entries: PackageEntry[]
  [key: string]: any
}

interface PackageContentsProps {
  packageData: PackageData
  config: any
  options: {
    artifactsPromise: Promise<any[]>
    docTypesForPicker: Array<{ id: number | string; name: string }>
  }
  updatePackageData: (data: PackageData) => void
  updateConfigData: (config: any) => void
}

const PackageContents: React.FC<PackageContentsProps> = ({
  packageData,
  config,
  options,
  updatePackageData,
  updateConfigData
}) => {
  const context = useServiceContext()
  const { showModal } = useModal()
  const { service, $rootScope } = context
  const { translate } = service.filter
  const [pckgData, setPckgData] = useState<PackageData | null>(null)
  const [configData, setConfigData] = useState<any>(null)
  const [conf, setConf] = useState<any>(null)
  const [submitted, setSubmitted] = useState(false)
  const [uniqueError, setUniqueError] = useState(false)
  const [allSelected, setAllSelected] = useState(false)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [isAnyUpdateDone, setIsAnyUpdateDone] = useState(false)
  const [isDoneSetPackageData, setIsDoneSettingPackageData] = useState(true)

  useEffect(() => {
    const unsubscribe = $rootScope.$on('validatePackageForms', (evt: any, packageData: PackageData) => {
      validate(packageData)
    })

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe()
      }
    }
  }, [])

  useEffect(() => {
    if (pckgData && configData && isInitialLoad) {
      setIsInitialLoad(false)
      const configDataLocal = clone(configData)
      let confData = clone(conf)
      if (!configDataLocal?.contents) {
        configDataLocal.contents = {}
        setConfigData((data: any) => ({ ...data, contents: configDataLocal.contents }))
      }

      confData = configDataLocal.contents
      setConf(confData)
    }
  }, [pckgData, configData])

  useEffect(() => {
    if (!isEqual(packageData, pckgData) && isDoneSetPackageData) {
      setIsDoneSettingPackageData(false)
      setPckgData(packageData)
    }
  }, [packageData])

  useEffect(() => {
    if (config) {
      setConfigData(config)
    }
  }, [config])

  useEffect(() => {
    updateConfigData(conf)
  }, [conf])

  useEffect(() => {
    updatePackageData(pckgData as PackageData)
    checkValidation(pckgData)
  }, [pckgData])

  const validate = (packageData: PackageData) => {
    setSubmitted(true)
    checkValidation(packageData)
  }

  const checkValidation = (packageData: PackageData | null) => {
    const status = hasEntries(packageData) && areAllUnique(packageData)
    $rootScope.$emit('contentValidation', !!status)
    return status
  }

  const hasEntries = (pckgDataLocal: PackageData | null = clone(pckgData)) => {
    return (pckgDataLocal?.entries?.length ?? 0) > 0
  }

  const areAllUnique = (pckgDataLocal: PackageData | null = clone(pckgData)) => {
    if (!pckgDataLocal?.entries) return true

    const unique = uniq(pckgDataLocal.entries, getEntryIdentity)

    if (unique?.length === pckgDataLocal?.entries?.length) {
      if (isAnyUpdateDone) {
        setIsAnyUpdateDone(false)
        setUniqueError(false)
      }
      return true
    } else {
      const nonUnique = difference(pckgDataLocal.entries, unique)

      each(unique, (entry) => {
        entry.isUnique = true
      })

      each(nonUnique, (entry) => {
        entry.isUnique = false
      })
      chain(nonUnique)
        .uniq()
        .each((nonUniqEntry) => {
          const foundEntry = find(unique, (uniqEntry) => {
            return getEntryIdentity(uniqEntry) === getEntryIdentity(nonUniqEntry)
          })
          if (foundEntry) {
            foundEntry.isUnique = false
          }
        })

      if (isAnyUpdateDone) {
        setIsAnyUpdateDone(false)
        setUniqueError(true)
      }
      return false
    }
  }

  const getEntryIdentity = (entry: PackageEntry) => {
    return entry?.zone?.toString() + entry?.section?.toString() + entry?.alias
  }

  const onSelectAll = (isChecked: boolean) => {
    setIsAnyUpdateDone(true)
    const pckgDataLocal = clone(pckgData)
    if (!pckgDataLocal) return

    each(pckgDataLocal?.entries, function (entry) {
      entry.selected = isChecked
    })
    setAllSelected(isChecked)
    setPckgData(pckgDataLocal)
  }

  const onSelect = (pckgDataLocal: PackageData | null = clone(pckgData)) => {
    setIsAnyUpdateDone(true)
    if (!pckgDataLocal) return

    const selectedEntries = filter(pckgDataLocal?.entries, (entry: PackageEntry) => entry.selected === true)
    const allSelectedData = pckgDataLocal?.entries?.length === selectedEntries?.length
    setAllSelected(allSelectedData)
  }

  const removeEntry = (entry: PackageEntry, index: number) => {
    const pckgDataLocal = clone(pckgData)
    if (!pckgDataLocal) return

    if (entry?.selected) {
      showModal(
        <PackageRemoveConfirmationModal
          removePackage={() => {
            setAllSelected(false)
            pckgDataLocal.entries = filter(pckgDataLocal?.entries, (entry: PackageEntry) => {
              return !entry?.selected
            })
            setIsAnyUpdateDone(true)
            setPckgData(pckgDataLocal)
          }}
          show={true}
          onClose={() => {}}
        />,
        { keepParentModalsOpen: true }
      )
    } else {
      pckgDataLocal.entries.splice(index, 1)
      setIsAnyUpdateDone(true)
      setPckgData(pckgDataLocal)
    }
  }

  const duplicateEntry = (entry: PackageEntry, index: number) => {
    const pckgDataLocal = clone(pckgData)
    if (!pckgDataLocal) return

    const selectedEntries = filter(pckgDataLocal?.entries, (e: PackageEntry) => e.selected === true)

    if (selectedEntries?.length > 1) {
      // Duplicate all selected entries
      // Create a map to track entries by a unique combination to avoid reference issues
      let entriesToDuplicate: Array<{ entry: PackageEntry; index: number }> = []

      each(selectedEntries, (selectedEntry) => {
        const entryIndex = findIndex(pckgDataLocal?.entries, (e) => e === selectedEntry)
        if (entryIndex !== -1) {
          entriesToDuplicate.push({
            entry: selectedEntry,
            index: entryIndex
          })
        }
      })

      // Sort by index in descending order (process from bottom to top)
      entriesToDuplicate = sortBy(entriesToDuplicate, 'index').reverse()

      // Process each entry from bottom to top
      each(entriesToDuplicate, (item) => {
        // Re-find the current index of this entry in case previous insertions shifted it
        let currentIndex = findIndex(
          pckgDataLocal?.entries,
          (e) =>
            e?.id === item?.entry?.id &&
            e?.zone === item?.entry?.zone &&
            e?.section === item?.entry?.section &&
            e?.alias === item?.entry?.alias &&
            e?.selected === item?.entry?.selected
        )

        if (currentIndex === -1) {
          currentIndex = item?.index // Fallback to original index
        }

        const indexNext = findFollowingNonDuplicateIndexInArray(pckgDataLocal?.entries, currentIndex, item?.entry, 1)
        const increment = indexNext - currentIndex
        const entryCopy = clone(item?.entry)
        Object.assign(entryCopy, {
          alias: createCopyName(item?.entry?.alias, increment),
          selected: false,
          isCopy: true,
          metadata: undefined
        })
        // Insert immediately to keep array consistent
        pckgDataLocal?.entries?.splice(indexNext, 0, entryCopy)
      })
    } else {
      // Duplicate single entry (original behaviour)
      const indexNext = findFollowingNonDuplicateIndexInArray(pckgDataLocal?.entries, index, entry, 1)
      const increment = indexNext - index
      const entryCopy = clone(entry)
      Object.assign(entryCopy, {
        alias: createCopyName(entry?.alias, increment),
        selected: false,
        isCopy: true,
        metadata: undefined
      })
      pckgDataLocal.entries.splice(indexNext, 0, entryCopy)
    }
    setIsAnyUpdateDone(true)
    setPckgData(pckgDataLocal)
  }

  const findFollowingNonDuplicateIndexInArray = (
    entries: PackageEntry[],
    index: number,
    item: PackageEntry,
    increment: number
  ): number => {
    if (entries?.length > index + increment) {
      const itemNext = entries[index + increment]
      const controlName = createCopyName(item?.alias, increment)

      if (itemNext?.alias === controlName) {
        return findFollowingNonDuplicateIndexInArray(entries, index, item, ++increment)
      } else {
        return index + increment
      }
    } else {
      return index + increment
    }
  }

  const createCopyName = (name: string, index: number) => {
    return translate(index > 1 ? 'COPY_OF_ITEM_WITH_INDEX' : 'COPY_OF_ITEM_NO_INDEX', {
      itemName: name,
      index: index
    })
  }

  const appendEntry = () => {
    options.artifactsPromise.then((artifactsMap) => {
      showModal(
        <AddFileplanArtifactModal
          packageData={pckgData}
          data={artifactsMap}
          addFileplans={(artifacts) => {
            const newEntries = chain(clone(artifacts))
              .map((artifact) => {
                return Object.assign(artifact, {
                  selected: false,
                  name: artifact?.name,
                  alias: artifact?.fileName,
                  docTypeIdOrig: artifact?.docTypeId
                })
              })
              .sortBy('name')
              .value()

            setIsAnyUpdateDone(true)
            setPckgData((data) => ({ ...data!, entries: data?.entries?.concat(newEntries) ?? newEntries }))
          }}
          show={true}
          onClose={() => {}}
        />,
        { keepParentModalsOpen: true }
      )
    })
  }

  return (
    <Formik initialValues={{}} onSubmit={() => {}}>
      <div className="form-group mcc-form-group">
        <label>{translate('SETTINGS_PACKAGE_CONTENTS')}</label>
        <div className="table-responsive">
          <table className="styled-table chita-table">
            <thead>
              <tr className="settings">
                <th className="has-checkbox">
                  <input checked={allSelected} type="checkbox" onChange={(evt) => onSelectAll(evt?.target?.checked)} />
                </th>
                <th className="artifact">{translate('ETMF_ARTIFACT')}</th>
                <th>{translate('SETTINGS_PACKAGE_CONTENTS_PLACEHOLDER_NAME')}</th>
                <th className="docType">{translate('DOCUMENT_TYPE')}</th>
                <th className="btns">{translate('ACTIONS')}</th>
              </tr>
            </thead>
            <tbody>
              {!pckgData?.entries || pckgData?.entries?.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center">
                    {translate('NO_RESULTS')}
                  </td>
                </tr>
              ) : (
                pckgData?.entries?.map((entry, index) => (
                  <tr key={`${entry?.id}-${entry?.zone}-${entry?.section}-${index}`}>
                    <td className="has-checkbox">
                      <input
                        checked={entry?.selected}
                        type="checkbox"
                        onChange={(evt) => {
                          const pckgDataLocal = clone(pckgData)
                          if (!pckgDataLocal) return
                          pckgDataLocal.entries[index].selected = evt?.target?.checked
                          onSelect(pckgDataLocal)
                          setPckgData(pckgDataLocal)
                        }}
                      />
                    </td>
                    <td className={`artifact ${entry.isCopy ? 'is-copy' : ''}`}>
                      <div className="ellipsis" title={entry.name}>
                        {entry?.name}
                      </div>
                    </td>
                    <td className={`has-input ${uniqueError && !entry?.isUnique ? 'has-error' : ''}`}>
                      <input
                        type="text"
                        className="md-input"
                        id={`d628763d-${entry?.name}`}
                        value={entry?.alias}
                        name="applyPackagesContentsAlias"
                        maxLength={500}
                        required={true}
                        onChange={(evt) => {
                          const pckgDataLocal = clone(pckgData)
                          if (!pckgDataLocal) return
                          pckgDataLocal.entries[index].alias = evt?.target?.value
                          setPckgData(pckgDataLocal)
                          setIsAnyUpdateDone(true)
                        }}
                      />
                    </td>
                    <td className="docType has-input">
                      <Select
                        id={`2d05204e-${entry?.name}`}
                        value={entry?.docTypeId}
                        name="applyPackagesContentsDocType"
                        placeholder={translate('SELECT_DOC_TYPE_PLACEHOLDER')}
                        onChange={(option: any) => {
                          const pckgDataLocal = clone(pckgData)
                          if (!pckgDataLocal) return
                          pckgDataLocal.entries[index].docTypeId = option ? option.value : null
                          setPckgData(pckgDataLocal)
                          setIsAnyUpdateDone(true)
                        }}
                        options={Utils.transformToSelectOptions(options?.docTypesForPicker, 'id', 'name', null, true)}
                        searchable
                        isClearable={false}
                      />
                    </td>
                    <td className="btns has-input whitespacenowrap actions-padding">
                      <TableMenu
                        item={pckgData as any}
                        setPckgData={setPckgData}
                        removeEntry={() => removeEntry(entry, index)}
                        duplicateEntry={() => duplicateEntry(entry, index)}
                        index={index}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div>
          <button
            id="6a9ca6a5-2eba-449d-ab15-e2c6563b3b08"
            style={{ textTransform: 'none' }}
            className="btn btn-chita btn-second"
            type="button"
            onClick={() => appendEntry()}
          >
            {translate('ADD_ITEM(S)')}
          </button>
        </div>

        {submitted && !hasEntries(pckgData) && (
          <p className="alert danger mt5">{translate('SETTINGS_PACKAGE_CONTENTS_REQUIRED_ERROR_MSG')}</p>
        )}

        {submitted && !areAllUnique(pckgData) && (
          <p className="alert danger mt5">{translate('SETTINGS_PACKAGE_CONTENTS_MUST_BE_UNIQUE_MSG')}</p>
        )}
      </div>
    </Formik>
  )
}

export default PackageContents
